import {
  CargoData,
  CarrierType,
  CarrierVisitDirection,
  OrderResponseDto,
  TransportData,
  UnloadingMethod,
} from '@planning/app/api'
import { DirectionalCargoDetailsType } from '../V2/organisms/DirectionalCargoDetailsSubForm'

export class OrderPageViewStore {
  mapToCargoData(directionalCargoDetails?: DirectionalCargoDetailsType): CargoData | undefined {
    if (!directionalCargoDetails) return undefined

    const cargoDetails = directionalCargoDetails.cargoDetails

    const grossWeight =
      !cargoDetails.grossWeight || isNaN(cargoDetails.grossWeight) ? 0 : cargoDetails.grossWeight
    const vgm = !cargoDetails.vgm || isNaN(cargoDetails.vgm) ? 0 : cargoDetails.vgm

    return {
      isEmpty: cargoDetails.isEmpty,
      content: cargoDetails.content,
      grossWeight,
      vgm,
      imoClasses: cargoDetails.dangerousGoods?.imoClasses ?? [],
      otherLabels: cargoDetails.dangerousGoods?.otherLabels,
      dangerousGoodsDefinitionIds:
        cargoDetails.dangerousGoods?.unNumbers
          ?.map(i => i.id)
          .filter((id): id is number => id !== undefined) ?? [],
      isOOG: directionalCargoDetails.oog?.isOog,
      overHeight: directionalCargoDetails.oog?.overHeight ?? 0,
      overLengthFront: directionalCargoDetails.oog?.overLengthFront ?? 0,
      overLengthRear: directionalCargoDetails.oog?.overLengthRear ?? 0,
      overWidthLeft: directionalCargoDetails.oog?.overWidthLeft ?? 0,
      overWidthRight: directionalCargoDetails.oog?.overWidthRight ?? 0,
      unloadingMethod: directionalCargoDetails.oog?.unloadingMethod ?? undefined,
    }
  }

  mapToTransportData(
    directionalCargoDetails?: DirectionalCargoDetailsType,
    commonCargoDetails?: DirectionalCargoDetailsType,
    direction?: CarrierVisitDirection,
    skipReleaseOrder?: boolean,
  ): TransportData | undefined {
    if (!directionalCargoDetails) return undefined

    const transport = directionalCargoDetails.transport

    if (!transport || !transport.carrierType) return undefined

    const carrierType = transport.carrierType ?? CarrierType.Universal

    const isOutboundTruck =
      carrierType === CarrierType.Truck && direction === CarrierVisitDirection.Outbound

    const hasCarrierVisitId = !!transport.carrierVisitId

    const carrierVisitId =
      carrierType !== CarrierType.Universal && hasCarrierVisitId
        ? transport.carrierVisitId
        : undefined

    const railcarId = carrierType === CarrierType.Train ? transport.railcarId : undefined
    const railTrackId = carrierType === CarrierType.Train ? transport.railTrackId : undefined

    const isReleased = isOutboundTruck ? !!transport.releaseExpires || skipReleaseOrder : false
    const releaseExpired =
      isOutboundTruck && transport.releaseExpires && transport.releaseExpires !== ''
        ? new Date(transport.releaseExpires).toISOString()
        : undefined

    const doorDirection =
      directionalCargoDetails?.doorDirection ?? commonCargoDetails?.doorDirection ?? undefined

    const stowagePosition =
      transport.bay && transport.row && transport.tier
        ? { bay: Number(transport.bay), row: Number(transport.row), tier: Number(transport.tier) }
        : undefined

    return {
      carrierType,
      carrierVisitId,
      railcarId,
      railTrackId,
      doorDirection,
      isReleased,
      releaseExpired,
      stowagePosition: transport.isFinalPosition ? undefined : stowagePosition,
      finalStowagePosition: transport.isFinalPosition ? stowagePosition : undefined,
    }
  }

  mapToDirectionalCargoDetails(order?: OrderResponseDto) {
    const stowagePosition = order?.finalStowagePosition ?? order?.stowagePosition

    const cargoDetails: DirectionalCargoDetailsType = {
      cargoDetails: {
        isEmpty: order?.isEmpty ?? false,
        content: order?.content ?? '',
        grossWeight: order?.grossWeight ?? 0,
        vgm: order?.vgm ?? 0,
        dangerousGoods: {
          isDangerous: order?.isDangerous ?? false,
          imoClasses: order?.imoClasses ?? [],
          otherLabels: order?.otherLabels ?? [],
          unNumbers: order?.dangerousGoodsDefinitions ?? [],
        },
      },
      doorDirection: order?.doorDirection,
      customs: order?.customs ?? [],
      oog: {
        isOog: order?.isOOG ?? false,
        overHeight: order?.overHeight ?? 0,
        overLengthFront: order?.overLengthFront ?? 0,
        overLengthRear: order?.overLengthRear ?? 0,
        overWidthLeft: order?.overWidthLeft ?? 0,
        overWidthRight: order?.overWidthRight ?? 0,
        unloadingMethod: order?.unloadingMethod ?? UnloadingMethod.Standard,
      },
      seals: {
        hasSeals: (order?.seals?.length ?? 0) > 0 || (order?.hasSeals ?? false),
        seals: order?.seals ?? [],
      },
      transport: {
        carrierName: order?.carrierName ?? '',
        carrierType: order?.carrierVisitType,
        carrierVisitId: order?.carrierVisitId ?? 0,
        railcarId: order?.railcarId ?? undefined,
        railTrackId: order?.railTrackId ?? '',
        releaseExpires: order?.releaseEnd ?? '',
        bay: stowagePosition?.bay?.toString(),
        row: stowagePosition?.row?.toString(),
        tier: stowagePosition?.tier?.toString(),
        isFinalPosition: !!order?.finalStowagePosition,
        orderId: order?.id,
      },
      stowageInstruction: order?.stowageInstruction,
    }

    return cargoDetails
  }
}
